import { memo, useState, useCallback, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Typography, Drawer, Box, Button } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import _map from 'lodash/map';
// Local files
import FilePreview from 'components/Common/FilePreview/FilePreview';
import DrawerSkeleton from 'components/Common/BaseDrawerSkeleton/BaseDrawerSkeleton';
import BaseEventsList from 'components/Analyses/Common/BaseEventsList/BaseEventsList';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useSixthAnalyses from 'hooks/useSixthAnalyses';
import useEvents from 'hooks/useEvents';
import { eventsSelectors } from "features/events";
import { sixthAnalysesSelectors } from 'features/sixthAnalyses';

const SixthAnalysisDrawer = () => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { sixth_analysis_id } = useParams();
  const { getSixthAnalysis, clearLocalActiveSixthAnalysis } = useSixthAnalyses();
  const { getSixthAnalysesEvents, clearLocalAnalysesEvents } = useEvents();
  const { data, pagination } = useCustomSelector(eventsSelectors.getAnalysesEventsData);
  const eventsFetching = useCustomSelector(eventsSelectors.analysesEventsIsFetching);
  const sixthAnalysisFetching = useCustomSelector(sixthAnalysesSelectors.sixthAnalysisIsFetching);
  const [open, setOpen] = useState(false);
  const [inputs, setInputs] = useState([]);
  const [result, setResult] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [firstTimeEventsOpened, setFirstTimeEventsOpened] = useState(true);

  const fetchSixthAnalysis = useCallback((id) => {
    setOpen(true);
    getSixthAnalysis({ id })
    .then(({ payload, error }) => {
      if (!error) {
        const { data: { sixth_analysis } } = payload;
        handleSetData(sixth_analysis);
      }
    })
  }, [getSixthAnalysis]);

  const handleSetData = (sixth_analysis) => {
    setInputs(sixth_analysis?.inputs);
    setResult(sixth_analysis?.result);
  };

  const handleExiting = () => {
    setInputs([]);
    setResult(null);
    setExpanded(false);
    setFirstTimeEventsOpened(true);
    navigate(`/flow/fl66p`);
    clearLocalActiveSixthAnalysis();
    clearLocalAnalysesEvents();
  };

  const handleClose = () => setOpen(false);

  const fetchSixthAnalysesEvents = useCallback((offset, sixth_analysis_id) => {
    getSixthAnalysesEvents({ offset, sixth_analysis_id })
  }, [ getSixthAnalysesEvents]);

  const handleChangeExpand = (_, isExpanded) => {
    setExpanded(isExpanded);
    if (firstTimeEventsOpened) {
      fetchSixthAnalysesEvents(0, sixth_analysis_id);
      setFirstTimeEventsOpened(false);
    }
  }

  useEffect(() => {
    if (!!sixth_analysis_id) fetchSixthAnalysis(sixth_analysis_id);
  }, [sixth_analysis_id, fetchSixthAnalysis]);

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      SlideProps={{ onExited: handleExiting }}
      onClose={handleClose}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
            Request log
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
        {sixthAnalysisFetching ? <DrawerSkeleton /> :
        <>
          <Typography variant='body1' sx={{ fontWeight: 600, textAlign: 'center', mb: '10px' }}>Job Id: {sixth_analysis_id}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center'  }}>
            {inputs.length !== 0 && <Box sx={{ mb: '30px' }}>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 400, mb: '10px' }}>Inputs:</Typography>
              {_map(inputs, (input) => 
                <FilePreview
                  key={input?.id}
                  type={input?.content_type}
                  name={input?.filename}
                  url={input?.url}
                />
              )}
            </Box>}
            {result?.id && <Box sx={{ mb: '30px' }}>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 400, mb: '10px' }}>Result:</Typography>
              <FilePreview
                key={result?.id}
                type={result?.content_type}
                name={result?.filename}
                url={result?.url}
              />
            </Box>}
          </Box>
          <BaseEventsList
            data={data}
            pagination={pagination}
            analysisId={sixth_analysis_id}
            eventsFetching={eventsFetching}
            fetchEvents={fetchSixthAnalysesEvents}
            expanded={expanded}
            handleChangeExpand={handleChangeExpand}
          />
        </>}
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <SixthAnalysisDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);